import { useFlag, useFlagsLoaded } from '@context'
import { IAccountingSuggestion } from '@root/query/suggestions'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  UNCERTAINTY_CODES,
  UNCERTAINTY_SECTION,
  UNCERTAINTY_SEVERITY,
  UncertaintyCode,
  UncertaintySection,
  isUncertaintyCode
} from './model'
import { SuggestionCardFormValues } from '../types'

interface SuggestionUncertaintyContextValue {
  enabled: boolean
  highlight: UncertaintySection | null
  issueCount: number
  hasError: boolean
  items: Array<{
    code: UncertaintyCode
    isIssue: boolean
  }>
  isOpen: (code: UncertaintyCode) => boolean
  dismiss: (code: UncertaintyCode) => void
  flash: (section: UncertaintySection) => void
  requestEdit: () => void
  focusAttachments: () => void
}

const SuggestionUncertaintyContext = createContext<SuggestionUncertaintyContextValue | null>(null)

const EMPTY_CONTEXT: SuggestionUncertaintyContextValue = {
  enabled: false,
  highlight: null,
  issueCount: 0,
  hasError: false,
  items: [],
  isOpen: () => false,
  dismiss: () => undefined,
  flash: () => undefined,
  requestEdit: () => undefined,
  focusAttachments: () => undefined
}

interface SuggestionUncertaintyProviderProps {
  suggestion: IAccountingSuggestion
  attachmentExempt?: boolean
  onEdit: () => void
  children: React.ReactNode
}

export const SuggestionUncertaintyProvider: React.FC<SuggestionUncertaintyProviderProps> = ({
  suggestion,
  attachmentExempt = false,
  onEdit,
  children
}) => {
  const flagsLoaded = useFlagsLoaded()
  const v3Algorithm = useFlag('accounting_suggestion_algorithm_v3')
  const enabled = flagsLoaded && v3Algorithm.enabled
  const { control } = useFormContext<SuggestionCardFormValues>()
  const attachmentIds = useWatch({ control, name: 'attachment_ids' }) ?? []
  const source = suggestion.import_data?.data_source
  const invoiceImport = source === 'SALES_INVOICE' || source === 'PURCHASE_INVOICE'
  const [dismissed, setDismissed] = useState<UncertaintyCode[]>([])
  const [highlight, setHighlight] = useState<UncertaintySection | null>(null)

  const flagged = useMemo(
    () => new Set((suggestion.uncertainty ?? []).filter(isUncertaintyCode)),
    [suggestion.uncertainty]
  )
  const flaggedKey = UNCERTAINTY_CODES.filter(code => flagged.has(code)).join(',')

  useEffect(() => {
    setDismissed([])
    setHighlight(null)
  }, [suggestion.id, flaggedKey])

  useEffect(() => {
    if (!highlight) return undefined
    const timeout = window.setTimeout(() => setHighlight(null), 1400)
    return () => window.clearTimeout(timeout)
  }, [highlight])

  const isOpen = useCallback(
    (code: UncertaintyCode) => {
      if (!enabled || dismissed.includes(code)) return false
      if (code === 'missing_attachment' && attachmentExempt) return false
      if (code === 'missing_attachment' && !invoiceImport) {
        return attachmentIds.length === 0
      }
      if (!flagged.has(code)) return false
      if (code === 'missing_attachment' && attachmentIds.length > 0) return false
      return true
    },
    [attachmentIds.length, attachmentExempt, dismissed, enabled, flagged, invoiceImport]
  )

  const dismiss = useCallback((code: UncertaintyCode) => {
    setDismissed(current => (current.includes(code) ? current : [...current, code]))
  }, [])

  const flash = useCallback(
    (section: UncertaintySection) => {
      setHighlight(section)
      const target = document.getElementById(`suggestion-${suggestion.id}-${section}`)
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    },
    [suggestion.id]
  )

  const focusAttachments = useCallback(() => {
    flash('attachments')
  }, [flash])

  const items = useMemo(
    () =>
      UNCERTAINTY_CODES.map(code => ({
        code,
        isIssue: isOpen(code)
      })),
    [isOpen]
  )

  const issueCount = items.filter(item => item.isIssue).length
  const hasError = items.some(item => item.isIssue && UNCERTAINTY_SEVERITY[item.code] === 'error')

  const value = useMemo<SuggestionUncertaintyContextValue>(
    () => ({
      enabled,
      highlight,
      issueCount,
      hasError,
      items,
      isOpen,
      dismiss,
      flash,
      requestEdit: onEdit,
      focusAttachments
    }),
    [
      dismiss,
      enabled,
      flash,
      focusAttachments,
      hasError,
      highlight,
      isOpen,
      issueCount,
      items,
      onEdit
    ]
  )

  return (
    <SuggestionUncertaintyContext.Provider value={value}>
      {children}
    </SuggestionUncertaintyContext.Provider>
  )
}

export const useSuggestionUncertainty = (): SuggestionUncertaintyContextValue =>
  useContext(SuggestionUncertaintyContext) ?? EMPTY_CONTEXT

export const uncertaintySection = (code: UncertaintyCode): UncertaintySection =>
  UNCERTAINTY_SECTION[code]
